import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  MapPin,
  BedDouble,
  Check,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Newsletter from "../components/Newsletter";
import EnquiryForm from "../components/EnquiryForm";
import { useToast } from "../hooks/use-toast";
import { feralStays } from "../mock";

const StayDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const venue = feralStays.venues.find((v) => v.slug === slug);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
  }, [slug]);

  if (!venue) {
    return (
      <div className="bg-[#f2e5de] min-h-screen">
        <Navbar />
        <div className="pt-40 pb-40 text-center">
          <p className="text-[#9b3d0a] feral-serif text-3xl">Stay not found.</p>
          <Link to="/feral-stays" className="text-[#9b3d0a] font-semibold mt-4 inline-block">
            Back to Feral Stays
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const inputCls =
    "w-full bg-white border border-[#d9c3a8] px-4 py-3 rounded-sm outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f]";

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[460px] w-full overflow-hidden">
        <img src={venue.heroImage} alt={venue.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <p className="text-[#e5816a] tracking-[0.2em] text-sm font-semibold mb-4">FERAL STAYS</p>
          <h1 className="feral-serif text-[#ebc754] text-5xl md:text-7xl">{venue.name}</h1>
          <p className="flex items-center gap-2 text-[#f2e5de]/90 mt-4 text-lg">
            <MapPin size={18} /> {venue.location}
          </p>
        </div>
      </section>

      <div className="feral-canvas px-6 pt-8">
        <div className="max-w-6xl mx-auto">
          <Link to="/feral-stays" className="inline-flex items-center gap-1 text-[#9b3d0a] hover:gap-2 transition-all text-sm font-semibold">
            <ChevronLeft size={18} /> All Feral Stays
          </Link>
        </div>
      </div>

      {/* Overview + gallery */}
      <section className="feral-canvas py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="rounded-md overflow-hidden shadow-xl aspect-[4/3]">
              <img src={venue.gallery[active]} alt={venue.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {venue.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-20 h-14 rounded overflow-hidden border-2 transition-colors ${
                    i === active ? "border-[#9b3d0a]" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="flex items-center gap-2 text-[#9b3d0a] font-semibold mb-4">
              <BedDouble size={18} /> {venue.sleeps}
            </p>
            <h2 className="feral-serif text-[#9b3d0a] text-3xl md:text-4xl mb-5">{venue.tagline}</h2>
            <p className="text-[#1e231f] text-lg leading-relaxed">{venue.description}</p>

            <h3 className="text-[#9a876f] tracking-[0.14em] text-xs font-bold uppercase mt-8 mb-4">
              Facilities
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {venue.facilities.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[#2e322c] text-sm">
                  <Check size={16} className="text-[#9b3d0a] mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Accommodation (at top) */}
      <section className="feral-texture bg-[#e2c7b0] py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-4">THE ACCOMMODATION</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl">Cottages &amp; spaces</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {venue.cottages.map((c) => (
              <div key={c.name} className="bg-white rounded-md overflow-hidden shadow-lg border border-[#d9c3a8] group flex flex-col">
                <div className="h-52 overflow-hidden">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="feral-serif text-[#9b3d0a] text-2xl mb-1">{c.name}</h3>
                  <p className="text-[#9b3d0a] font-semibold text-sm mb-3">{c.sleeps}</p>
                  <p className="text-[#6e5646] text-sm leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Retreats + enquire */}
      <section id="enquire" className="feral-texture bg-[#e2c7b0] py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-4">
              {feralStays.retreatSection.eyebrow}
            </p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-4">
              Retreats at {venue.name}
            </h2>
            <p className="text-[#1e231f] text-lg leading-relaxed max-w-3xl mx-auto">
              {feralStays.retreatSection.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {feralStays.retreatSection.retreats.map((r) => (
              <div key={r.title} className="group bg-white rounded-md overflow-hidden shadow-lg border border-[#d9c3a8]">
                <div className="h-40 overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <h3 className="feral-serif text-[#9b3d0a] text-xl mb-2">{r.title}</h3>
                  <p className="text-[#6e5646] text-sm leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <EnquiryForm defaultVenue={venue.name} title={`Enquire about ${venue.name}`} />
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default StayDetail;
